import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { Btn, Input, Card } from "../components/UI";
import { FLabel } from "./engagement/FormControls";
import { toast } from "sonner";
import { ShieldCheck, KeyRound } from "lucide-react";

// Magic-link password reset (audit item 81). User arrives here from the
// forgot-password email with a 15-min signed token. Sets a new password.

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password/verify", { token, new_password: pw });
      setDone(true);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] p-6">
      <Card className="w-full max-w-md p-8" data-testid="reset-password-page">
        {done ? (
          <>
            <div className="flex justify-center mb-3"><span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#E8F5E9] text-[#2E7D32]"><ShieldCheck size={28} /></span></div>
            <h1 className="text-center font-heading text-xl font-bold text-[#3E2723]">Password updated</h1>
            <p className="mt-2 text-center text-sm text-[#5D4037]">Your password has been reset. Sign in with the new one.</p>
            <div className="mt-6 flex justify-center">
              <Btn data-testid="reset-goto-login" onClick={() => navigate("/login")}>Go to sign in</Btn>
            </div>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="flex justify-center mb-2"><span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F5F2EB] text-[#4E342E]"><KeyRound size={28} /></span></div>
            <div className="text-center">
              <h1 className="font-heading text-xl font-bold text-[#3E2723]">Choose a new password</h1>
              <p className="mt-1 text-xs text-[#7B6157]">Links expire 15 minutes after they are sent. At least 8 characters with an upper, a lower, a digit, and a symbol.</p>
            </div>
            {!token && (
              <div className="rounded-lg bg-[#FFEBEE] p-3 text-xs text-[#C62828]" data-testid="reset-missing-token">
                Missing token. Request a fresh reset link from the sign-in page.
              </div>
            )}
            <div><FLabel>New password</FLabel>
              <Input type="password" data-testid="reset-pw-input" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} />
            </div>
            <div><FLabel>Confirm new password</FLabel>
              <Input type="password" data-testid="reset-pw-confirm" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={8} />
            </div>
            <Btn data-testid="reset-submit" type="submit" className="w-full" disabled={busy || !token}>
              {busy ? "Updating…" : "Reset password"}
            </Btn>
            <div className="text-center text-xs text-[#7B6157]">
              <Link to="/login" className="hover:underline">Back to sign in</Link>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
